import { useState, type ElementType } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MapPin, Clock, User, CheckSquare, Camera, Package,
  Clock3, FileText, Navigation, ChevronRight, CheckCircle2,
  Phone, Briefcase, Receipt, Lock, Tag, ScanLine, DollarSign,
  Sparkles, Copy, Plus, XCircle, ChevronDown, ChevronUp, Loader2,
  AlertTriangle,
} from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';
import { formatTime, formatDate, cn } from '../lib/utils.js';
import { useJobParts } from '../hooks/useParts.js';
import type {
  OnsiteJob, OnsiteChecklistItem, JobStatus, JobType, JobPriority,
} from '../../../api/src/types/onsite-types.js';

const TERMINAL_STATUSES: JobStatus[] = ['completed', 'invoiced', 'closed', 'cancelled'];

const STATUS_ACTIONS: Partial<Record<JobStatus, { label: string; next: JobStatus }>> = {
  new:        { label: 'Schedule Job',           next: 'scheduled'  },
  scheduled:  { label: 'Start Route (En Route)', next: 'en_route'   },
  dispatched: { label: 'Start Route (En Route)', next: 'en_route'   },
  en_route:   { label: 'Arrived On Site',        next: 'on_site'    },
  on_site:    { label: 'Complete Job',           next: 'completed'  },
  completed:  { label: 'Mark as Invoiced',       next: 'invoiced'   },
  invoiced:   { label: 'Close Job',              next: 'closed'     },
};

const TABS = [
  { id: 'checklist', label: 'Checklist', icon: CheckSquare },
  { id: 'photos',    label: 'Photos',    icon: Camera       },
  { id: 'parts',     label: 'Parts',     icon: Package      },
  { id: 'time',      label: 'Time',      icon: Clock3       },
  { id: 'notes',     label: 'Notes',     icon: FileText     },
];

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  new:        { label: 'New',        color: 'bg-purple-500/20 text-purple-400'  },
  scheduled:  { label: 'Scheduled',  color: 'bg-blue-500/20 text-blue-400'     },
  dispatched: { label: 'Dispatched', color: 'bg-cyan-500/20 text-cyan-400'     },
  en_route:   { label: 'En Route',   color: 'bg-amber-500/20 text-amber-400'   },
  on_site:    { label: 'On Site',    color: 'bg-green-500/20 text-green-400'   },
  completed:  { label: 'Completed',  color: 'bg-muted-foreground/20 text-muted-foreground'   },
  invoiced:   { label: 'Invoiced',   color: 'bg-emerald-500/20 text-emerald-400' },
  closed:     { label: 'Closed',     color: 'bg-secondary/20 text-muted-foreground'   },
  cancelled:  { label: 'Cancelled',  color: 'bg-red-500/20 text-red-400'       },
};

export default function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState('checklist');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['job', id],
    queryFn: () => onsiteApiFetch<{ job: OnsiteJob }>(`/api/v1/onsite/jobs/${id}`),
    enabled: !!id,
  });

  const updateJob = useMutation({
    mutationFn: (patch: Partial<OnsiteJob>) =>
      onsiteApiFetch<{ job: OnsiteJob }>(`/api/v1/onsite/jobs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job', id] }),
  });

  const invoiceJob = useMutation({
    mutationFn: () =>
      onsiteApiFetch<{ job: OnsiteJob }>(`/api/v1/onsite/jobs/${id}/invoice`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job', id] }),
  });

  const closeJob = useMutation({
    mutationFn: () =>
      onsiteApiFetch<{ job: OnsiteJob }>(`/api/v1/onsite/jobs/${id}/close`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job', id] }),
  });

  const cancelJob = useMutation({
    mutationFn: (reason: string) =>
      onsiteApiFetch<{ job: OnsiteJob }>(`/api/v1/onsite/jobs/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      setShowCancel(false);
      setCancelReason('');
      qc.invalidateQueries({ queryKey: ['job', id] });
    },
  });

  const job = data?.job;

  if (isLoading || !job) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  const nextAction = STATUS_ACTIONS[job.status];

  async function handleStatusAdvance() {
    if (!nextAction) return;

    if (nextAction.next === 'on_site') {
      navigate(`/jobs/${id}/checkin`);
      return;
    }

    if (nextAction.next === 'completed') {
      navigate(`/jobs/${id}/signature`);
      return;
    }

    if (nextAction.next === 'invoiced') {
      await invoiceJob.mutateAsync();
      return;
    }

    if (nextAction.next === 'closed') {
      await closeJob.mutateAsync();
      return;
    }

    await updateJob.mutateAsync({ status: nextAction.next });
  }

  async function toggleChecklist(item: OnsiteChecklistItem) {
    const newChecklist = job!.checklist.map(c =>
      c.id === item.id
        ? { ...c, completed: !c.completed, completed_at: !c.completed ? new Date().toISOString() : undefined }
        : c
    );
    await updateJob.mutateAsync({ checklist: newChecklist });
  }

  const statusCfg = STATUS_CONFIG[job.status] ?? STATUS_CONFIG.new;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b border-border bg-card p-4">
        <div className="mb-1 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-foreground line-clamp-1">{job.title}</h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {job.psa_ticket_number && <span>Ticket #{job.psa_ticket_number}</span>}
              {job.job_type && job.job_type !== 'other' && (
                <span className="flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  {job.job_type.replace('_', ' ')}
                </span>
              )}
            </div>
          </div>
          <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${statusCfg.color}`}>
            {statusCfg.label}
          </span>
        </div>

        <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 shrink-0" />
            <span>{job.customer_name || 'No customer'}</span>
          </div>
          {job.customer_address && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0" />
                <span className="line-clamp-1">{job.customer_address}</span>
              </div>
              <a
                href={`https://maps.apple.com/?q=${encodeURIComponent(job.customer_address)}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-primary hover:bg-primary/10"
              >
                <Navigation className="h-3 w-3" />
                Directions
              </a>
            </div>
          )}
          {job.contact_name && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 shrink-0" />
              <span>{job.contact_name}{job.contact_phone ? ` (${job.contact_phone})` : ''}</span>
            </div>
          )}
          {job.assigned_tech_name && (
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 shrink-0" />
              <span>Assigned: {job.assigned_tech_name}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 shrink-0" />
            <span>{formatDate(job.scheduled_start)} {formatTime(job.scheduled_start)} - {formatTime(job.scheduled_end)}</span>
            {job.estimated_duration_minutes && (
              <span className="text-xs text-muted-foreground">({job.estimated_duration_minutes}min est.)</span>
            )}
          </div>
        </div>

        {job.description && (
          <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{job.description}</p>
        )}

        {/* Status advance button */}
        {nextAction && (
          <button
            onClick={handleStatusAdvance}
            disabled={updateJob.isPending || invoiceJob.isPending || closeJob.isPending}
            className={cn(
              'mt-4 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-foreground disabled:opacity-50',
              nextAction.next === 'invoiced' ? 'bg-emerald-600 hover:bg-emerald-700'
                : nextAction.next === 'closed' ? 'bg-secondary hover:bg-secondary'
                : 'bg-primary hover:bg-primary/90'
            )}
          >
            {(updateJob.isPending || invoiceJob.isPending || closeJob.isPending) ? (
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <>
                {nextAction.next === 'invoiced' && <Receipt className="h-5 w-5" />}
                {nextAction.next === 'closed' && <Lock className="h-5 w-5" />}
                {nextAction.label}
                <ChevronRight className="h-5 w-5" />
              </>
            )}
          </button>
        )}

        {(job.status === 'closed' || job.status === 'cancelled') && (
          <div className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-secondary px-4 py-3 text-muted-foreground">
            {job.status === 'closed' ? <Lock className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
            <span className="font-medium capitalize">{job.status}</span>
          </div>
        )}

        {/* Cancel button — hidden for terminal statuses */}
        {!TERMINAL_STATUSES.includes(job.status) && (
          <button
            onClick={() => setShowCancel(true)}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/30 px-4 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10"
          >
            <XCircle className="h-4 w-4" />
            Cancel Job
          </button>
        )}
      </div>

      {/* Cancel modal */}
      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowCancel(false)}>
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-4" onClick={e => e.stopPropagation()}>
            <h3 className="mb-1 text-base font-semibold text-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              Cancel Job
            </h3>
            <p className="mb-3 text-sm text-muted-foreground">
              This marks the job as cancelled and notifies the customer. Please provide a reason.
            </p>
            <textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation (required)"
              rows={4}
              className="w-full rounded-lg border border-border bg-secondary p-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none resize-none"
            />
            {cancelJob.isError && (
              <p className="mt-2 text-xs text-red-400">{(cancelJob.error as Error).message}</p>
            )}
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => { setShowCancel(false); setCancelReason(''); }}
                className="flex-1 rounded-lg border border-border py-2 text-sm text-muted-foreground hover:bg-secondary"
              >
                Keep Job
              </button>
              <button
                onClick={() => cancelReason.trim() && cancelJob.mutate(cancelReason.trim())}
                disabled={!cancelReason.trim() || cancelJob.isPending}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-red-600 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {cancelJob.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-border bg-card/50">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex flex-1 flex-col items-center gap-1 px-2 py-3 text-xs font-medium transition-colors',
              activeTab === tab.id
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'checklist' && (
          <ChecklistTab job={job} onToggle={toggleChecklist} />
        )}
        {activeTab === 'photos' && (
          <QuickLinkTab icon={Camera} label="Capture Photos" to={`/jobs/${id}/photos`} />
        )}
        {activeTab === 'parts' && (
          <PartsTab jobId={id!} />
        )}
        {activeTab === 'time' && (
          <QuickLinkTab icon={Clock3} label="Log Time" to={`/jobs/${id}/time`} />
        )}
        {activeTab === 'notes' && (
          <NotesTab job={job} onSave={notes => updateJob.mutateAsync({ notes })} />
        )}

        {/* AI Assistant panel */}
        <AiAssistantPanel job={job} onApplyChecklist={items => updateJob.mutateAsync({ checklist: items })} />
      </div>
    </div>
  );
}

// ─── AI Assistant panel ────────────────────────────────────────

interface PartSuggestion {
  part_number?: string;
  description: string;
  quantity?: number;
  unit_cost?: number;
  reason?: string;
}

interface TriageSuggestion {
  job_type?: JobType;
  priority?: JobPriority;
  estimated_duration_minutes?: number;
  reasoning?: string;
}

function AiAssistantPanel({ job, onApplyChecklist }: {
  job: OnsiteJob;
  onApplyChecklist: (items: OnsiteChecklistItem[]) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [checklistSuggestions, setChecklistSuggestions] = useState<string[] | null>(null);
  const [resolution, setResolution] = useState<string[] | null>(null);
  const [partSuggestions, setPartSuggestions] = useState<PartSuggestion[] | null>(null);
  const [triage, setTriage] = useState<TriageSuggestion | null>(null);
  const [summaryCopied, setSummaryCopied] = useState(false);
  const qc = useQueryClient();

  const summarize = useMutation({
    mutationFn: () =>
      onsiteApiFetch<{ summary: string }>(`/api/v1/onsite/jobs/${job.id}/summarize`, { method: 'POST' }),
    onSuccess: d => setSummary(d.summary),
  });

  const generateChecklist = useMutation({
    mutationFn: () =>
      onsiteApiFetch<{ checklist: Array<{ id: string; label: string; completed: boolean }> }>(
        `/api/v1/onsite/jobs/${job.id}/generate-checklist`,
        { method: 'POST' }
      ),
    onSuccess: d => {
      const items = Array.isArray(d.checklist) ? d.checklist : [];
      setChecklistSuggestions(items.map(i => i.label));
    },
  });

  const suggestResolution = useMutation({
    mutationFn: () =>
      onsiteApiFetch<{ suggestions: string | null }>(`/api/v1/onsite/jobs/${job.id}/suggest-resolution`, {
        method: 'POST',
      }),
    onSuccess: d => {
      // Backend returns a single text blob (often a numbered list); split into items.
      if (!d.suggestions) { setResolution([]); return; }
      const lines = d.suggestions
        .split(/\r?\n/)
        .map(l => l.replace(/^\s*(?:\d+[.)]|\-|\*)\s*/, '').trim())
        .filter(Boolean);
      setResolution(lines.length > 0 ? lines : [d.suggestions]);
    },
  });

  const partsSuggest = useMutation({
    mutationFn: () =>
      onsiteApiFetch<{ suggestions: PartSuggestion[] | null }>(`/api/v1/onsite/jobs/${job.id}/parts-suggest`, {
        method: 'POST',
      }),
    onSuccess: d => setPartSuggestions(d.suggestions ?? []),
  });

  const triageJob = useMutation({
    mutationFn: () =>
      onsiteApiFetch<{ triage: TriageSuggestion | null }>(`/api/v1/onsite/jobs/${job.id}/triage`, { method: 'POST' }),
    onSuccess: d => setTriage(d.triage),
  });

  const applyTriage = useMutation({
    mutationFn: (patch: Partial<OnsiteJob>) =>
      onsiteApiFetch(`/api/v1/onsite/jobs/${job.id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job', job.id] }),
  });

  const addPart = useMutation({
    mutationFn: (p: PartSuggestion) =>
      onsiteApiFetch('/api/v1/onsite/parts', {
        method: 'POST',
        body: JSON.stringify({
          job_id: job.id,
          part_number: p.part_number ?? 'AI-SUGGESTED',
          description: p.description,
          quantity: p.quantity ?? 1,
          unit_cost: p.unit_cost,
          billable: true,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parts', job.id] }),
  });

  async function applyChecklistSuggestions() {
    if (!checklistSuggestions) return;
    const newItems: OnsiteChecklistItem[] = checklistSuggestions.map((label, idx) => ({
      id: `ai-${Date.now()}-${idx}`,
      label,
      completed: false,
    }));
    await onApplyChecklist([...job.checklist, ...newItems]);
    setChecklistSuggestions(null);
  }

  const showTriage = job.job_type === 'other';
  const showGenerateChecklist = job.checklist.length === 0;

  return (
    <div className="mt-6 rounded-xl border border-border bg-card">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Sparkles className="h-4 w-4 text-primary" />
          AI Assistant
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="border-t border-border p-4 space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <AiButton
              icon="✦"
              label="Summarize Job"
              pending={summarize.isPending}
              onClick={() => summarize.mutate()}
            />
            {showGenerateChecklist && (
              <AiButton
                icon="✦"
                label="Generate Checklist"
                pending={generateChecklist.isPending}
                onClick={() => generateChecklist.mutate()}
              />
            )}
            <AiButton
              icon="✦"
              label="Suggest Resolution"
              pending={suggestResolution.isPending}
              onClick={() => suggestResolution.mutate()}
            />
            <AiButton
              icon="✦"
              label="Suggest Parts"
              pending={partsSuggest.isPending}
              onClick={() => partsSuggest.mutate()}
            />
            {showTriage && (
              <AiButton
                icon="✦"
                label="Triage Job"
                pending={triageJob.isPending}
                onClick={() => triageJob.mutate()}
              />
            )}
          </div>

          {/* Error aggregation */}
          {[summarize, generateChecklist, suggestResolution, partsSuggest, triageJob].some(m => m.isError) && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              AI assistant unavailable
            </div>
          )}

          {/* Summary card */}
          {summary && (
            <AiResultCard title="Summary" onClose={() => setSummary(null)}>
              <p className="text-sm text-foreground whitespace-pre-wrap">{summary}</p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(summary);
                  setSummaryCopied(true);
                  setTimeout(() => setSummaryCopied(false), 1500);
                }}
                className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline"
              >
                {summaryCopied ? <CheckCircle2 className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {summaryCopied ? 'Copied' : 'Copy'}
              </button>
            </AiResultCard>
          )}

          {/* Checklist suggestions */}
          {checklistSuggestions && (
            <AiResultCard title="Proposed Checklist" onClose={() => setChecklistSuggestions(null)}>
              <ul className="list-disc pl-5 space-y-1 text-sm text-foreground">
                {checklistSuggestions.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <button
                onClick={applyChecklistSuggestions}
                className="mt-3 flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="h-3 w-3" />
                Apply All
              </button>
            </AiResultCard>
          )}

          {/* Resolution suggestions */}
          {resolution && (
            <AiResultCard title="Resolution Suggestions" onClose={() => setResolution(null)}>
              <ol className="list-decimal pl-5 space-y-1 text-sm text-foreground">
                {resolution.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            </AiResultCard>
          )}

          {/* Part suggestions */}
          {partSuggestions && (
            <AiResultCard title="Suggested Parts" onClose={() => setPartSuggestions(null)}>
              <div className="space-y-2">
                {partSuggestions.map((p, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-secondary p-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">{p.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.part_number && <>#{p.part_number} · </>}
                        qty {p.quantity ?? 1}
                        {p.unit_cost != null && <> · ${p.unit_cost.toFixed(2)}</>}
                      </p>
                      {p.reason && <p className="text-xs text-muted-foreground/80 mt-0.5">{p.reason}</p>}
                    </div>
                    <button
                      onClick={() => addPart.mutate(p)}
                      className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                    >
                      Add to Job
                    </button>
                  </div>
                ))}
              </div>
            </AiResultCard>
          )}

          {/* Triage result */}
          {triage && (
            <AiResultCard title="Triage Recommendation" onClose={() => setTriage(null)}>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Type</p>
                  <p className="font-medium text-foreground capitalize">{triage.job_type?.replace('_', ' ') ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Priority</p>
                  <p className="font-medium text-foreground capitalize">{triage.priority ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="font-medium text-foreground">{triage.estimated_duration_minutes ? `${triage.estimated_duration_minutes}min` : '—'}</p>
                </div>
              </div>
              {triage.reasoning && <p className="mt-2 text-xs text-muted-foreground">{triage.reasoning}</p>}
              <button
                onClick={() => {
                  applyTriage.mutate({
                    ...(triage.job_type && { job_type: triage.job_type }),
                    ...(triage.priority && { priority: triage.priority }),
                    ...(triage.estimated_duration_minutes && { estimated_duration_minutes: triage.estimated_duration_minutes }),
                  });
                  setTriage(null);
                }}
                className="mt-3 flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="h-3 w-3" />
                Apply Recommendation
              </button>
            </AiResultCard>
          )}
        </div>
      )}
    </div>
  );
}

function AiButton({ icon, label, pending, onClick }: {
  icon: string; label: string; pending: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={pending}
      className="flex items-center justify-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-xs font-medium text-primary hover:bg-primary/20 disabled:opacity-50"
    >
      {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <span>{icon}</span>}
      {label}
    </button>
  );
}

function AiResultCard({ title, onClose, children }: {
  title: string; onClose: () => void; children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-secondary p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          {title}
        </h4>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <XCircle className="h-3.5 w-3.5" />
        </button>
      </div>
      {children}
    </div>
  );
}

function ChecklistTab({
  job,
  onToggle,
}: {
  job: OnsiteJob;
  onToggle: (item: OnsiteChecklistItem) => void;
}) {
  const done = job.checklist.filter(i => i.completed).length;
  return (
    <div>
      <p className="mb-3 text-sm text-muted-foreground">
        {done} / {job.checklist.length} completed
      </p>
      {job.checklist.length === 0 && (
        <p className="text-center text-sm text-muted-foreground py-8">No checklist items for this job</p>
      )}
      <div className="space-y-2">
        {job.checklist.map(item => (
          <button
            key={item.id}
            onClick={() => onToggle(item)}
            className={cn(
              'flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors',
              item.completed
                ? 'border-green-500/30 bg-green-500/10'
                : 'border-border bg-secondary hover:border-border'
            )}
          >
            <div className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2',
              item.completed
                ? 'border-green-500 bg-green-500 text-foreground'
                : 'border-border'
            )}>
              {item.completed && <CheckCircle2 className="h-4 w-4" />}
            </div>
            <span className={cn('text-sm', item.completed ? 'text-muted-foreground line-through' : 'text-foreground')}>
              {item.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function QuickLinkTab({ icon: Icon, label, to }: { icon: ElementType; label: string; to: string }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between rounded-xl border border-border bg-secondary p-4 hover:bg-secondary"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-primary" />
        <span className="font-medium text-foreground">{label}</span>
      </div>
      <ChevronRight className="h-5 w-5 text-muted-foreground" />
    </Link>
  );
}

function PartsTab({ jobId }: { jobId: string }) {
  const navigate = useNavigate();
  const { data, isLoading } = useJobParts(jobId);
  const parts = data?.parts ?? [];
  const totalBillable = parts
    .filter(p => p.billable && p.unit_cost != null)
    .reduce((sum, p) => sum + p.quantity * (p.unit_cost ?? 0), 0);

  return (
    <div className="space-y-3">
      {/* Scan & Sell button */}
      <button
        onClick={() => navigate(`/jobs/${jobId}/scan`)}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-4 text-base font-semibold text-foreground hover:bg-primary/90"
      >
        <ScanLine className="h-5 w-5" />
        Scan & Sell Item
      </button>

      {/* Manage parts link */}
      <Link
        to={`/jobs/${jobId}/parts`}
        className="flex items-center justify-between rounded-xl border border-border bg-secondary p-4 hover:bg-secondary"
      >
        <div className="flex items-center gap-3">
          <Package className="h-5 w-5 text-primary" />
          <div>
            <span className="font-medium text-foreground">Manage Parts</span>
            {parts.length > 0 && (
              <span className="ml-2 rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium text-primary">
                {parts.length}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground" />
      </Link>

      {/* Billable subtotal */}
      {totalBillable > 0 && (
        <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <DollarSign className="h-4 w-4" />
            <span>Billable parts subtotal</span>
          </div>
          <span className="text-sm font-bold text-green-400">${totalBillable.toFixed(2)}</span>
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}
    </div>
  );
}

function NotesTab({ job, onSave }: { job: OnsiteJob; onSave: (notes: string) => void }) {
  const [notes, setNotes] = useState(job.notes);
  return (
    <div>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Add job notes..."
        rows={8}
        className="w-full rounded-xl border border-border bg-secondary p-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none resize-none"
      />
      <button
        onClick={() => onSave(notes)}
        className="mt-3 w-full rounded-xl bg-primary px-4 py-3 font-medium text-foreground hover:bg-primary/90"
      >
        Save Notes
      </button>
    </div>
  );
}
